import { useState, useEffect } from "react";
import {
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
} from "@mui/material";
import useClient from "../../hooks/useClient";
import useProject from "../../hooks/useProject";
import useSupervisors from "../../hooks/useSupervisors";
import useSupervisorReps from "../../hooks/useSupervisorReps";

const RepDashboardFilters = ({ filters, onChange }) => {
  const [localFilters, setLocalFilters] = useState(filters);
  const [projects, setProjects] = useState([]);
  const [loadingProjects, setLoadingProjects] = useState(false);
  const { clients, getClients } = useClient();
  const { projects: projectsFromHook, getProjects } = useProject();
  const { supervisors = [] } = useSupervisors();
  const { reps = [] } = useSupervisorReps(localFilters?.supervisorId);

  useEffect(() => {
    getClients();
  }, []);

  // Load projects when client filter changes
  useEffect(() => {
    if (localFilters.clientId) {
      loadProjects();
    } else {
      setProjects([]);
      setLocalFilters((prev) => ({ ...prev, projectId: null }));
    }
  }, [localFilters.clientId]);

  // Update local projects state when projectsFromHook changes
  useEffect(() => {
    if (projectsFromHook) {
      setProjects(projectsFromHook);
    }
  }, [projectsFromHook]);

  const loadProjects = async () => {
    try {
      setLoadingProjects(true);
      if (localFilters.clientId) {
        await getProjects({
          clientId: localFilters.clientId,
          isActive: true,
        });
      } else {
        setProjects([]);
      }
    } catch (err) {
      console.error("Error loading projects:", err);
      setProjects([]);
    } finally {
      setLoadingProjects(false);
    }
  };

  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 5 }, (_, i) => currentYear - i);
  const months = [
    { value: 1, label: "January" },
    { value: 2, label: "February" },
    { value: 3, label: "March" },
    { value: 4, label: "April" },
    { value: 5, label: "May" },
    { value: 6, label: "June" },
    { value: 7, label: "July" },
    { value: 8, label: "August" },
    { value: 9, label: "September" },
    { value: 10, label: "October" },
    { value: 11, label: "November" },
    { value: 12, label: "December" },
  ];

  const handleYearChange = (e) => {
    const newFilters = { ...localFilters, year: e.target.value };
    setLocalFilters(newFilters);
    onChange(newFilters);
  };

  const handleMonthChange = (e) => {
    const newFilters = { ...localFilters, month: e.target.value };
    setLocalFilters(newFilters);
    onChange(newFilters);
  };

  const handleClientChange = (e) => {
    const newFilters = {
      ...localFilters,
      clientId: e.target.value || null,
      projectId: null,
    };
    setLocalFilters(newFilters);
    onChange(newFilters);
  };

  const handleProjectChange = (e) => {
    const newFilters = {
      ...localFilters,
      projectId: e.target.value || null,
    };
    setLocalFilters(newFilters);
    onChange(newFilters);
  };

  const handleSupervisorChange = (e) => {
    const newFilters = {
      ...localFilters,
      supervisorId: e.target.value || null,
      repId: null,
    };
    setLocalFilters(newFilters);
    onChange(newFilters);
  };

  const handleRepChange = (e) => {
    const newFilters = {
      ...localFilters,
      repId: e.target.value || null,
    };
    setLocalFilters(newFilters);
    onChange(newFilters);
  };

  const handleStartDateChange = (e) => {
    const newFilters = { ...localFilters, startDate: e.target.value || null };
    setLocalFilters(newFilters);
    onChange(newFilters);
  };

  const handleEndDateChange = (e) => {
    const newFilters = { ...localFilters, endDate: e.target.value || null };
    setLocalFilters(newFilters);
    onChange(newFilters);
  };

  return (
    <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
      <FormControl sx={{ minWidth: 120 }}>
        <InputLabel>Year</InputLabel>
        <Select
          value={localFilters.year}
          onChange={handleYearChange}
          label="Year"
        >
          {years.map((year) => (
            <MenuItem
              key={year}
              value={year}
            >
              {year}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <FormControl sx={{ minWidth: 150 }}>
        <InputLabel>Month</InputLabel>
        <Select
          value={localFilters.month}
          onChange={handleMonthChange}
          label="Month"
        >
          {months.map((month) => (
            <MenuItem
              key={month.value}
              value={month.value}
            >
              {month.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <TextField
        label="Start Date"
        type="date"
        value={localFilters.startDate || ""}
        onChange={handleStartDateChange}
        InputLabelProps={{
          shrink: true,
        }}
        sx={{ minWidth: 180 }}
      />

      <TextField
        label="End Date"
        type="date"
        value={localFilters.endDate || ""}
        onChange={handleEndDateChange}
        InputLabelProps={{
          shrink: true,
        }}
        sx={{ minWidth: 180 }}
      />

      <FormControl sx={{ minWidth: 250 }}>
        <InputLabel>Clients</InputLabel>
        <Select
          value={localFilters.clientId || ""}
          onChange={handleClientChange}
          label="Clients"
        >
          <MenuItem value="">
            <em>All Clients</em>
          </MenuItem>
          {clients?.map((client) => (
            <MenuItem
              key={client.id}
              value={client.id}
            >
              {client.name}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {/* Projects Filter */}
      <FormControl
        sx={{ minWidth: 250 }}
        disabled={!localFilters.clientId}
      >
        <InputLabel>Projects</InputLabel>
        <Select
          value={localFilters.projectId || ""}
          onChange={handleProjectChange}
          label="Projects"
        >
          <MenuItem value="">
            <em>All Projects</em>
          </MenuItem>
          {projects?.map((project) => (
            <MenuItem
              key={project.id}
              value={project.id}
            >
              {project.name}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <FormControl sx={{ minWidth: 250 }}>
        <InputLabel>Supervisor</InputLabel>
        <Select
          value={localFilters.supervisorId || ""}
          onChange={handleSupervisorChange}
          label="Supervisor"
        >
          <MenuItem value="">
            <em>All Supervisors</em>
          </MenuItem>
          {supervisors?.map((supervisor) => (
            <MenuItem
              key={supervisor.id}
              value={supervisor.id}
            >
              {supervisor.fullName ||
                supervisor.name ||
                [supervisor.firstName, supervisor.lastName]
                  .filter(Boolean)
                  .join(" ") ||
                supervisor.email ||
                supervisor.id}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <FormControl sx={{ minWidth: 250 }}>
        <InputLabel>Field Rep</InputLabel>
        <Select
          value={localFilters.repId || ""}
          onChange={handleRepChange}
          label="Field Rep"
          disabled={!localFilters.supervisorId}
        >
          <MenuItem value="">
            <em>All Reps</em>
          </MenuItem>
          {reps?.map((rep) => (
            <MenuItem
              key={rep.id}
              value={rep.id}
            >
              {rep.fullName ||
                rep.name ||
                [rep.firstName, rep.lastName].filter(Boolean).join(" ") ||
                rep.email ||
                rep.id}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    </Box>
  );
};

export default RepDashboardFilters;
